import { ConnectButton } from '@rainbow-me/rainbowkit';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Link } from "react-router-dom";
import { RiDownload2Line } from "@remixicon/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { Card, CardContent } from "@/components/card";
import { Button } from "@/components/button";
import { mainConfig } from "@/configs/main.config";

// Silo Data
const pricingPlans = mainConfig.siloPlans;
const products = mainConfig.siloProducts.individual;

export default function SiloIndividualPage() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/silo">SILO</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Individual</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">Individual</h1>
              </div>

              <h2 className="text-2xl font-semibold mb-4">SILO Storage Pricing for Individual Users</h2>

              <div className="mb-8">
                <h3 className="text-xl font-medium mb-3">Introduction</h3>
                <p className="text-muted-foreground">
                  SILO offers flexible storage plans for individual users through SILO Drive, SILO Photos, and other future apps from SILO.
                  Individual users can select from a variety of plans based on their storage needs.
                </p>
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                <div className="lg:col-span-2">
                  <div className="flex items-center mb-6">
                    <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                    <h3 className="text-xl font-medium">Products</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {products.map((product, index) => (
                      <Card key={index} className="bg-sidebar border border-border overflow-hidden hover:border-[#FF8A3C]/50 transition-all duration-300 h-full flex flex-col">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF8A3C] to-[#FF8A3C]/70 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>

                        <CardContent className="p-6 flex flex-col h-full">
                          <div className="flex items-center mb-4">
                            <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center mr-4 overflow-hidden">
                              <img
                                src={product.image}
                                alt={product.name}
                                width={48}
                                height={48}
                                className="object-contain"
                              />
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold">{product.name}</h4>
                              <div className="text-xs px-2 py-1 rounded-full bg-muted inline-block">
                                {product.status === "Available" ? (
                                  <span className="text-green-500">{product.status}</span>
                                ) : (
                                  <span className="text-amber-500">{product.status}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <p className="text-sm text-muted-foreground mb-4 flex-grow">
                            {product.description}
                          </p>

                          <div className="mt-auto">
                            {product.links.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {product.links.map((link, linkIndex) => (
                                  <div key={linkIndex}>
                                    {link.status ? (
                                      <div className="text-sm text-muted-foreground flex items-center">
                                        <span className="mr-2">{link.platform}:</span>
                                        <span className="text-amber-500">{link.status}</span>
                                      </div>
                                    ) : (
                                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                                        <Button variant="outline" size="sm" className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]">
                                          <RiDownload2Line className="mr-1 size-4" />
                                          {link.platform}
                                        </Button>
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground">
                                Coming soon
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <div className="flex items-center mb-6">
                    <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                    <h3 className="text-xl font-medium">Pricing Plans</h3>
                  </div>

                  <Card className="bg-sidebar border border-border overflow-hidden">
                    <CardContent className="p-6">
                      <Tabs defaultValue="monthly" className="mb-4">
                        <TabsList className="mb-6 w-full">
                          <TabsTrigger value="monthly" className="flex-1">Monthly</TabsTrigger>
                          <TabsTrigger value="yearly" className="flex-1">Yearly</TabsTrigger>
                        </TabsList>

                        <TabsContent value="monthly">
                          <div className="space-y-4">
                            {pricingPlans.map((plan, index) => (
                              <div key={index} className="p-4 border border-border rounded-lg hover:border-[#FF8A3C]/50 transition-colors">
                                <div className="flex justify-between items-center mb-2">
                                  <div className="font-medium">{plan.name}</div>
                                  <div className="text-[#FF8A3C] font-medium">{plan.monthlyPrice}</div>
                                </div>
                                <div className="text-sm text-muted-foreground">{plan.storage}</div>
                              </div>
                            ))}
                          </div>
                        </TabsContent>

                        <TabsContent value="yearly">
                          <div className="space-y-4">
                            {pricingPlans.map((plan, index) => (
                              <div key={index} className="p-4 border border-border rounded-lg hover:border-[#FF8A3C]/50 transition-colors">
                                <div className="flex justify-between items-center mb-2">
                                  <div className="font-medium">{plan.name}</div>
                                  <div className="text-[#FF8A3C] font-medium">{plan.yearlyPrice}</div>
                                </div>
                                <div className="text-sm text-muted-foreground mb-1">{plan.storage}</div>
                                <div className="text-xs text-muted-foreground">Price per year</div>
                              </div>
                            ))}
                          </div>
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}





